import {
  PaymentOrderByWithRelationInput,
  PaymentsQuery,
  PaymentStatus,
  PaymentWhereInput,
} from '@/__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { useDebouncedValue, useLocalStorage } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useMemo, useState } from 'react';
import { DeletePaymentDocument, PaymentsDocument } from '../graphql/queries';

const paymentStatusColors = {
  [PaymentStatus.Pending]: 'gray',
  [PaymentStatus.Review]: 'orange',
  [PaymentStatus.Failed]: 'red',
  [PaymentStatus.Completed]: 'green',
};

const paymentStatusTranslations = {
  [PaymentStatus.Pending]: 'Čakajúca',
  [PaymentStatus.Review]: 'Nespárovaná',
  [PaymentStatus.Failed]: 'Zlyhala',
  [PaymentStatus.Completed]: 'Zaplatená',
};

// const PaymentModal = ({ payment }: { payment: PaymentsQuery['payments'][number] }) => {
//   const [filterStatus, setFilterStatus] = useState<OrderWhereInput>({
//     total: { equals: payment.amount },
//     payments: { some: { status: { not: { equals: PaymentStatus.Completed } } } },
//   });

//   const [where] = useDebouncedValue(filterStatus, 500);

//   const { data, loading } = useQuery(ordersDocument, {
//     variables: {
//       where,
//       orderBy: { createdAt: SortOrder.Desc },
//     },
//   });

//   const [updatePayment, updatePaymentMutation] = useMutation(UpdatePaymentDocument, {
//     refetchQueries: [PaymentsDocument, ordersDocument],
//     awaitRefetchQueries: true,
//   });

//   const [deletePayment, deletePaymentMutation] = useMutation(DeletePaymentDocument, {
//     refetchQueries: [PaymentsDocument],
//     awaitRefetchQueries: true,
//   });

//   return (
//     <Stack>
//       <TextInput
//         placeholder="Meno zákazníka"
//         value={filterStatus.customer?.is?.name?.contains ?? ''}
//         onChange={(event) => {
//           setFilterStatus((prev) => ({
//             ...prev,
//             customer:
//               event.currentTarget.value.length > 0
//                 ? { is: { name: { contains: event.currentTarget.value } } }
//                 : undefined,
//           }));
//         }}
//       />
//       <DataTable
//         withTableBorder
//         striped
//         highlightOnHover
//         borderRadius="sm"
//         minHeight={200}
//         records={data?.orders ?? []}
//         fetching={loading}
//         columns={[
//           {
//             accessor: 'package',
//             title: 'Balík',
//           },
//           {
//             accessor: 'customer.name',
//             title: 'Meno zákazníka',
//           },
//           {
//             accessor: 'status',
//             title: 'Stav',
//             render: ({ status }) => (
//               <Badge color={orderStatusColors[status]}>{orderStatusTranslations[status]}</Badge>
//             ),
//           },
//           {
//             accessor: 'paymentMethod',
//             title: 'Spôsob platby',
//             render: ({ paymentMethod }) => paymentMethod?.name,
//           },
//           {
//             accessor: 'total',
//             title: 'Suma objednávky',
//             render: ({ total }) => `${total} €`,
//           },
//           {
//             accessor: 'payment',
//             title: 'Platba',
//             noWrap: true,
//             render: ({ payments }) => {
//               const orderPayment = payments?.at(0);

//               if (!orderPayment) return null;

//               return (
//                 <Group>
//                   <Badge color={paymentStatusColors[orderPayment.status]}>
//                     {paymentStatusTranslations[orderPayment.status]} ({orderPayment.amount} €)
//                   </Badge>
//                   <Badge>{orderPayment.method}</Badge>
//                 </Group>
//               );
//             },
//           },
//           {
//             accessor: 'actions',
//             textAlign: 'right',
//             render: (order) => (
//               <ActionIcon
//                 size="sm"
//                 variant="subtle"
//                 color="green"
//                 loading={updatePaymentMutation.loading || deletePaymentMutation.loading}
//                 onClick={async () => {
//                   const orderPayment = order.payments?.at(0);

//                   if (!orderPayment) return null;

//                   await updatePayment({
//                     variables: {
//                       where: { id: orderPayment.id },
//                       data: {
//                         //referenceId: { set: payment.referenceId },
//                         method: { set: payment.method },
//                         status: { set: PaymentStatus.Completed },
//                         metadata: { set: { ...payment.metadata } },
//                         order: {
//                           update: {
//                             data: {
//                               customer: { update: { data: { metadata: { ibanType: 'F' } } } },
//                             },
//                           },
//                         },
//                       },
//                     },
//                   });

//                   await deletePayment({
//                     variables: { where: { id: payment.id } },
//                   });

//                   modals.closeAll();
//                 }}
//               >
//                 <IconCheck size={16} />
//               </ActionIcon>
//             ),
//           },
//         ]}
//       />
//     </Stack>
//   );
// };

export const PaymentList = () => {
  const [filterStatus, setFilterStatus] = useLocalStorage<PaymentWhereInput>({
    key: 'payment-list-filter',
    defaultValue: {
      status: { equals: PaymentStatus.Review },
    },
  });
  const [where] = useDebouncedValue(filterStatus, 500);

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<PaymentsQuery['payments'][number]>
  >({
    columnAccessor: 'updatedAt',
    direction: 'desc',
  });

  const orderBy = useMemo<PaymentOrderByWithRelationInput>(() => {
    switch (sortStatus.columnAccessor) {
      default:
        return { [sortStatus.columnAccessor]: sortStatus.direction };
    }
  }, [sortStatus]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data: count } = useQuery(PaymentsDocument, {
    variables: { where },
  });

  const { data, loading } = useQuery(PaymentsDocument, {
    variables: {
      where,
      skip: pageSize * page - pageSize,
      take: pageSize,
      orderBy,
    },
  });

  const [deletePayment, deletePaymentMutation] = useMutation(DeletePaymentDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const [selectedRecords, setSelectedRecords] = useState<PaymentsQuery['payments']>([]);

  return (
    <Stack>
      <Paper p="md" withBorder component={Group}>
        <Group>
          <Select
            clearable
            placeholder="Typ platby..."
            data={data?.payments
              .map((payment) => payment.method)
              .filter((value, index, self) => self.indexOf(value) === index)
              .map((value) => ({
                value,
                label: value,
              }))}
            value={filterStatus.method?.equals ?? ''}
            onChange={(value) => {
              setFilterStatus((prev) => ({
                ...prev,
                method: value ? { equals: value } : undefined,
              }));
            }}
            onClear={() => {
              setFilterStatus((prev) => ({ ...prev, method: undefined }));
            }}
          />
          <Select
            clearable
            placeholder="Stav platby..."
            data={Object.entries(paymentStatusTranslations).map(([value, label]) => ({
              value,
              label,
            }))}
            value={filterStatus.status?.equals ?? ''}
            onChange={(value) => {
              setFilterStatus((prev) => ({
                ...prev,
                status: value ? { equals: value as PaymentStatus } : undefined,
              }));
            }}
            onClear={() => {
              setFilterStatus((prev) => ({ ...prev, status: undefined }));
            }}
          />
          <Button
            color="red"
            disabled={selectedRecords.length === 0}
            loading={deletePaymentMutation.loading}
            onClick={() => {
              modals.openConfirmModal({
                title: `Zmazať platby ${selectedRecords.length}`,
                children: (
                  <Stack>
                    <Alert color="red">Naozaj chcete zmazať tieto platby?</Alert>
                    <Text>
                      {selectedRecords
                        .map((payment) => `${payment.referenceId} (${payment.amount} €)`)
                        .join(', ')}
                    </Text>
                  </Stack>
                ),
                onConfirm: async () => {
                  await Promise.allSettled(
                    selectedRecords.map((payment) =>
                      deletePayment({ variables: { where: { id: payment.id } } })
                    )
                  );
                },
                labels: { cancel: 'Nie', confirm: 'Áno' },
              });
            }}
          >
            Zmazať {selectedRecords.length} platieb
          </Button>
        </Group>
      </Paper>
      <DataTable
        withTableBorder
        striped
        highlightOnHover
        textSelectionDisabled
        borderRadius="sm"
        minHeight={200}
        records={data?.payments ?? []}
        fetching={loading}
        totalRecords={count?.payments.length ?? 0}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={setPage}
        recordsPerPageOptions={[5, 10, 25, 50, 75, 100]}
        onRecordsPerPageChange={setPageSize}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        selectedRecords={selectedRecords}
        onSelectedRecordsChange={setSelectedRecords}
        columns={[
          {
            accessor: 'method',
            title: 'Typ',
            sortable: true,
          },
          {
            accessor: 'status',
            title: 'Stav',
            sortable: true,
            render: ({ status }) => (
              <Badge color={paymentStatusColors[status]}>{paymentStatusTranslations[status]}</Badge>
            ),
          },
          {
            accessor: 'amount',
            title: 'Suma',
            sortable: true,
            render: ({ amount }) => `${amount} €`,
          },
          {
            accessor: 'referenceId',
            title: 'ID platby',
            sortable: true,
          },
          {
            accessor: 'metadata.partnerName',
            title: 'Meno platiteľa',
          },
          {
            accessor: 'metadata.partnerAccount',
            title: 'IBAN platiteľa',
          },
          {
            accessor: 'metadata.description',
            title: 'Info pre príjemcu',
          },
          {
            accessor: 'createdAt',
            title: 'Dátum',
            render: ({ createdAt }) => dayjs(createdAt).format('DD.MM.YYYY HH:mm'),
            sortable: true,
          },
          {
            accessor: 'actions',
            textAlign: 'right',
            render: (payment) => (
              <Group ml="auto">
                {/* <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="green"
                  onClick={() => {
                    modals.open({ children: <PaymentModal payment={payment} />, size: 'auto' });
                  }}
                >
                  <IconEye size={16} />
                </ActionIcon> */}
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="red"
                  loading={deletePaymentMutation.loading}
                  onClick={() => {
                    modals.openConfirmModal({
                      title: 'Zmazať platbu',
                      children: 'Naozaj chcete zmazať túto platbu?',
                      onConfirm: () => {
                        deletePayment({ variables: { where: { id: payment.id } } });
                      },
                      labels: { confirm: 'Zmazať', cancel: 'Zrušiť' },
                    });
                  }}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ),
          },
        ]}
        rowExpansion={{
          expandable: ({ record }) => !!record.metadata && typeof record.metadata === 'object',
          content: ({ record }) => (
            <Table variant="vertical" layout="fixed" withTableBorder>
              <Table.Tbody>
                {Object.entries(record.metadata).map(([key, value]) => (
                  <Table.Tr key={key}>
                    <Table.Th w={200}>{key}</Table.Th>
                    <Table.Td>{String(value)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ),
        }}
      />
    </Stack>
  );
};
